import { RichTextView } from "components/DesignSystem/RichTextEditor/RichTextView";
import { InvoiceCustomerModal } from "components/InvoiceCustomerModal/InvoiceCustomerModal";
import { Form } from "formik";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  useGetCurrenciesQuery,
  useGetEntityInvoiceQuery,
} from "store/apis/invoices";
import { InvoiceFormSection } from "components/InvoiceForm/InvoiceFormSection";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import { Address } from "components/Address/Address";
import { formatDate } from "utils/formatDate";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import {
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { TableUI } from "components/design/TableUI";
import { ConditionalLink } from "components/conditionalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { LineItemInfoTable } from "components/LineItems/LineItemInfoTable";
import { Tag } from "components/DesignSystem/Tag/Tag";
import {
  CREDIT_NOTE_STATUS_LABEL,
  CREDIT_NOTE_STATUS_TAG_TYPE,
  CreditNoteStatusType,
} from "constants/creditNotes";
import { CreditNote } from "store/apis/creditNotes";
import ToolTip from "components/design/toolTip";
import { LinkBreak } from "components/icons/LinkBreak";
import { Button } from "components/DesignSystem/Button/Button";
import { InvoiceAttachments } from "components/InvoiceForm/InvoiceAttachments";
import { ExchangeRateSummary } from "components/design/ExchangeRateSummary";
import { USD } from "constants/currency";

const INFO_GRID_CLASS = "t-grid t-grid-cols-2 t-gap-x-3 t-gap-y-4";

const creditNoteColumnHelper = createColumnHelper<CreditNote>();

const getCreditNoteColumns = () => {
  const columns: ColumnDef<CreditNote, any>[] = [
    creditNoteColumnHelper.accessor("date", {
      header: "Creation Date",
      size: 40,
      cell: (info) => formatDate(info.getValue()),
    }),
    creditNoteColumnHelper.accessor("amount", {
      header: "Amount",
      size: 30,
      cell: (info) => <AmountSuperScript amount={info.getValue()} />,
    }),
    creditNoteColumnHelper.accessor("status", {
      header: "Status",
      size: 30,
      cell: (info) => {
        const status = info.getValue() as CreditNoteStatusType;
        return (
          <Tag
            tagType={CREDIT_NOTE_STATUS_TAG_TYPE[status]}
            icon={false}
            size="small"
            rounded
          >
            {CREDIT_NOTE_STATUS_LABEL[status]}
          </Tag>
        );
      },
    }),
  ];
  return columns;
};

export const CreditNotesTable = ({
  creditNotes,
}: {
  creditNotes: CreditNote[];
}) => {
  const columns = getCreditNoteColumns();

  const table = useReactTable({
    columns,
    data: creditNotes,
    getCoreRowModel: getCoreRowModel(),
  });

  return <TableUI size="regular" table={table} />;
};

export const InvoiceInfo = () => {
  const [customerId, setCustomerId] = useState<string | null | "NEW">("");
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const group = useCurrentGroupContext();
  const { search } = useLocation();
  const entityId = useCurrentEntityId();

  const { data: invoice } = useGetEntityInvoiceQuery(
    {
      groupId: group?.uuid!,
      entityId: entityId!,
      invoiceId,
    },
    { skip: !group?.uuid || !entityId }
  );

  const { data: currenciesResponse = {} } = useGetCurrenciesQuery();

  const currentCurrency = Object.entries(currenciesResponse).find(
    ([code, name]) => code === invoice?.currency
  );

  const exchangeRate = invoice?.payment_record_amount?.exchange_rate;
  const usdEquivalent = exchangeRate
    ? Number(invoice?.due_balance) / Number(exchangeRate)
    : null;

  if (!invoice) {
    return null;
  }

  let currencyLabel = "-";
  if (currentCurrency) {
    currencyLabel = `${currentCurrency[0]} - ${currentCurrency[1]}`;
  }

  const hasPaymentInstructions = Boolean(
    invoice.payment_instructions || invoice.payment_link
  );

  return (
    <Form>
      <div className="t-flex t-flex-col t-gap-12 t-pb-10">
        <InvoiceFormSection title="Customer details">
          <div className={INFO_GRID_CLASS}>
            <InfoItem label="Name">
              <ConditionalLink
                className="t-text-purple"
                to={`/books/invoices-and-customers/customers/${invoice?.customer?.uuid}${search}`}
              >
                {invoice?.customer?.company_name}
              </ConditionalLink>
            </InfoItem>
            <InfoItem label="Email">{invoice?.customer?.email}</InfoItem>
            <InfoItem label="Phone">{invoice?.customer?.phone}</InfoItem>
            {invoice?.customer?.billing_address && (
              <InfoItem label="Address">
                <Address address={invoice?.customer?.billing_address} />
              </InfoItem>
            )}
          </div>
        </InvoiceFormSection>

        <InvoiceFormSection title="Invoice details">
          <InfoItem label="Invoice title">{invoice?.title || "-"}</InfoItem>
          <div className={INFO_GRID_CLASS}>
            <InfoItem label="Invoice number">
              {invoice?.invoice_number}
            </InfoItem>
            <InfoItem label="Invoice date">
              {invoice?.invoice_date ? formatDate(invoice?.invoice_date) : "-"}
            </InfoItem>
            <InfoItem label="Due date">
              {invoice?.due_date ? formatDate(invoice?.due_date) : "-"}
            </InfoItem>
            <InfoItem label="Preferred currency">{currencyLabel}</InfoItem>
            <InfoItem label="PO number">{invoice?.po_number || "-"}</InfoItem>
            <InfoItem label="PO issue date">
              {invoice?.po_issue_date
                ? formatDate(invoice?.po_issue_date)
                : "-"}
            </InfoItem>
          </div>
        </InvoiceFormSection>

        <InvoiceFormSection title="Item details">
          <LineItemInfoTable invoice={invoice} />
        </InvoiceFormSection>

        <InvoiceFormSection title="Discount">
          <div className={INFO_GRID_CLASS}>
            <InfoItem label="Discount">
              {invoice?.discount_type === "PERCENT" ? (
                `${invoice?.discount || 0}%`
              ) : (
                <AmountSuperScript
                  amount={Number(invoice.discount)}
                  currencyCode={invoice.currency}
                />
              )}
            </InfoItem>
            {!invoice?.has_item_level_tax && (
              <InfoItem label="Tax">
                {invoice?.tax_type === "PERCENT" ? (
                  `${invoice?.tax || 0}%`
                ) : (
                  <AmountSuperScript
                    amount={Number(invoice.tax)}
                    currencyCode={invoice.currency}
                  />
                )}
              </InfoItem>
            )}
            {!invoice?.has_item_level_tax && (
              <InfoItem label="Advance paid">
                <AmountSuperScript
                  amount={Number(invoice?.paid_amount) || 0}
                  currencyCode={invoice.currency}
                />
              </InfoItem>
            )}
          </div>
        </InvoiceFormSection>

        {invoice?.currency && invoice.currency !== USD && (
          <ExchangeRateSummary
            rate={exchangeRate}
            currency={invoice.currency}
            date={invoice.invoice_date}
            isCustom={invoice.has_exchange_rate_changed}
            totalAmount={Number(invoice.due_balance)}
            usdEquivalent={usdEquivalent}
          />
        )}

        {invoice.linked_credit_debit_notes &&
          invoice.linked_credit_debit_notes.length > 0 && (
            <InvoiceFormSection title="Credit notes">
              <CreditNotesTable
                creditNotes={invoice.linked_credit_debit_notes}
              />
            </InvoiceFormSection>
          )}

        <InvoiceFormSection title="Additional details">
          <InfoItem label="Notes">
            {invoice?.notes ? <RichTextView value={invoice.notes} /> : "-"}
          </InfoItem>
          <InfoItem label="Payment instructions">
            {hasPaymentInstructions ? (
              <>
                {invoice.payment_instructions && (
                  <RichTextView value={invoice.payment_instructions} />
                )}
                {invoice.payment_link && (
                  <p className="t-m-0">
                    Payment link (if paying online):{" "}
                    <span className="t-font-bold">{invoice.payment_link}</span>
                  </p>
                )}
              </>
            ) : (
              "-"
            )}
          </InfoItem>
          <InfoItem label="Terms">
            {invoice?.terms ? <RichTextView value={invoice.terms} /> : "-"}
          </InfoItem>
          <InfoItem label="Footer">
            {invoice?.footer ? <RichTextView value={invoice.footer} /> : "-"}
          </InfoItem>
        </InvoiceFormSection>

        {(invoice.attachments?.length ?? 0) > 0 && (
          <InvoiceFormSection title="Attachments">
            <InvoiceAttachments invoice={invoice} readOnly />
          </InvoiceFormSection>
        )}
      </div>
      <InvoiceCustomerModal
        customerId={customerId}
        setCustomerId={setCustomerId}
      />
    </Form>
  );
};
